import React from 'react';
import { AlertTriangleIcon, CheckIcon, LoaderIcon, PlayIcon, XIcon } from 'lucide-react';
import { scenarios } from '../../../data/beheer/system';
import type { ScenarioRunState } from '../../../hooks/useScenarioRunner';
import type { ScenarioResult } from '../../../types/beheer';
const RESULT: Record<ScenarioResult, {
  icon: typeof CheckIcon;
  tone: string;
}> = {
  geslaagd: {
    icon: CheckIcon,
    tone: 'text-accent'
  },
  waarschuwing: {
    icon: AlertTriangleIcon,
    tone: 'text-amber'
  },
  mislukt: {
    icon: XIcon,
    tone: 'text-alarm'
  }
};
interface ScenarioTestsProps {
  runs: Record<string, ScenarioRunState>;
  onRun: (id: string) => void;
}
export function ScenarioTests({
  runs,
  onRun
}: ScenarioTestsProps) {
  return <div className="no-scrollbar overflow-x-auto rounded-3xl border border-white/[0.07] bg-night-900" data-id="element-466" data-mp="2w6cop5toyo6k">
      <table className="w-full min-w-[900px] text-left text-[14px]" data-id="element-467" data-mp="28fz583p69xnl">
        <thead data-id="element-468" data-mp="23j8pxil6z638">
          <tr className="border-b border-white/[0.06] text-[12px] text-ink-muted" data-id="element-469" data-mp="23hfezr3jpzpf">
            <th scope="col" className="px-5 py-3 font-medium" data-id="element-470" data-mp="1ig5eug8ezai3">Scenario</th>
            <th scope="col" className="px-3 py-3 font-medium" data-id="element-471" data-mp="1ne6yp3dnzdum">Verwacht</th>
            <th scope="col" className="px-3 py-3 font-medium" data-id="element-472" data-mp="1nccbfiex79nd">Laatste run</th>
            <th scope="col" className="px-3 py-3 font-medium" data-id="element-473" data-mp="1s5oyvp7v5aej">Resultaat</th>
            <th scope="col" className="px-5 py-3" data-id="element-474" data-mp="1w5pcj7n51hkj"><span className="sr-only" data-id="element-475" data-mp="1w8v36rbf0rid">Actie</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.05]" data-id="element-476" data-mp="zi2n3tohezgl">
          {scenarios.map(s => {
          const r = runs[s.id];
          const res = RESULT[r.result];
          const ResIcon = res.icon;
          return <tr key={s.id} className="align-top" data-id="element-477" data-mp="18jivjvvqkri6">
                <th scope="row" className="px-5 py-4 font-normal" data-id="element-478" data-mp="1dh2a6wwcj6an">
                  <span className="block font-medium text-ink" data-id="element-479" data-mp="2jwusdmt2w9uu">{s.name}</span>
                  <span className="block text-[12px] text-ink-muted" data-id="element-480" data-mp="2jyphldbduo4q">{s.checks}</span>
                </th>
                <td className="max-w-[300px] px-3 py-4 text-ink-soft" data-id="element-481" data-mp="ot6i7xhhgozt">{s.expected}</td>
                <td className="whitespace-nowrap px-3 py-4 tabular-nums text-ink-muted" data-id="element-482" data-mp="ov171mvvl196">
                  {r.lastRun}
                  <span className="block text-[12px]" data-id="element-483" data-mp="yr44wgjp2dqb">{r.duration}</span>
                </td>
                <td className="min-w-[220px] px-3 py-4" aria-live="polite" data-id="element-484" data-mp="ts9mvgtjwt79">
                  {r.running ? <span className="block" data-id="element-485" data-mp="625llg3u55jf">
                      <span className="flex items-center gap-2 text-ink" data-id="element-486" data-mp="638etuomrk2i">
                        <LoaderIcon className="h-3.5 w-3.5 animate-spin" aria-hidden data-id="element-487" data-mp="14n9jguiua77" />
                        Stap {Math.min(r.step + 1, s.steps.length)} van {s.steps.length}
                      </span>
                      <span className="mt-0.5 block text-[12px] text-ink-muted" data-id="element-488" data-mp="b102udm5mm1f">{s.steps[Math.min(r.step, s.steps.length - 1)]}</span>
                      <span className="mt-2 block h-1 overflow-hidden rounded-full bg-white/[0.06]" data-id="element-489" data-mp="3jfieepbb0jps">
                        <span className="block h-full rounded-full bg-accent transition-[width] duration-300 ease-snappy" style={{
                    width: `${(r.step + 1) / s.steps.length * 100}%`
                  }} data-id="element-490" data-mp="3ege57yodqlzg" />
                      </span>
                    </span> : <span className="block" data-id="element-491" data-mp="1p0leaj0lsmrc">
                      <span className={`flex items-center gap-1.5 font-medium ${res.tone}`} data-id="element-492" data-mp="1p1mpqolj7b9l">
                        <ResIcon className="h-3.5 w-3.5" aria-hidden data-id="element-493" data-mp="2crqsktqwb6zj" /> {r.result}
                      </span>
                      {r.note && <span className="mt-0.5 block text-[12px] text-ink-muted" data-id="element-494" data-mp="2hno8iyy8ybbs">{r.note}</span>}
                    </span>}
                </td>
                <td className="whitespace-nowrap px-5 py-4 text-right" data-id="element-495" data-mp="28o14s6i1otfk">
                  <button type="button" disabled={r.running} onClick={() => onRun(s.id)} className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full bg-white/[0.06] px-3.5 text-[13px] font-medium text-ink transition-colors duration-150 hover:bg-white/[0.11] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:cursor-not-allowed disabled:opacity-40" data-id="element-496" data-mp="saukkm04z3ml">
                    <PlayIcon className="h-3.5 w-3.5" aria-hidden data-id="element-497" data-mp="yt8igy4407a4" /> Nu draaien
                  </button>
                </td>
              </tr>;
        })}
        </tbody>
      </table>
    </div>;
}